import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { User } from "@phosphor-icons/react";
import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@navi-soft/ui/button";
import {
  ScreenToolbar,
  ScreenToolbarSpacer,
} from "@navi-soft/ui/screen-toolbar";
import { fetchCart, fetchMe, logout, type MeResponse } from "@/lib/api";
import { cn } from "@/lib/cn";

const LOGO = "/brand/consortium.svg";

type NavId =
  | "home"
  | "find"
  | "catalog"
  | "parts"
  | "fleet"
  | "service"
  | "field";

type NavItem = {
  id: NavId;
  to: string;
  labelKey:
    | "navNews"
    | "navFind"
    | "navCatalog"
    | "navParts"
    | "navFleet"
    | "navService"
    | "navField";
  /** Spec §8 later / not built yet — visible for IA, stub page. */
  later?: boolean;
};

const NAV: NavItem[] = [
  { id: "home", to: "/", labelKey: "navNews" },
  { id: "find", to: "/find", labelKey: "navFind" },
  { id: "catalog", to: "/catalog", labelKey: "navCatalog" },
  { id: "parts", to: "/parts", labelKey: "navParts" },
  { id: "fleet", to: "/fleet", labelKey: "navFleet", later: true },
  { id: "service", to: "/service", labelKey: "navService", later: true },
  { id: "field", to: "/field", labelKey: "navField", later: true },
];

function activeNavId(pathname: string): NavId {
  if (pathname.startsWith("/find")) return "find";
  if (pathname.startsWith("/catalog") || pathname.startsWith("/network") || pathname.startsWith("/configure")) {
    return "catalog";
  }
  if (
    pathname.startsWith("/parts") ||
    pathname.startsWith("/offer") ||
    pathname.startsWith("/cart")
  ) {
    return "parts";
  }
  if (pathname.startsWith("/fleet")) return "fleet";
  if (pathname.startsWith("/service")) return "service";
  if (pathname.startsWith("/field")) return "field";
  return "home";
}

/**
 * Partner portal shell — light chrome (not Yard workbench).
 * Full §8 IA in the rail; later sections are stubs.
 */
export function ForumShell({ children }: { children: ReactNode }) {
  const t = useTranslations("app");
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = activeNavId(pathname);

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const res = await fetchMe();
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
  const me = meQuery.data;
  const authenticated = Boolean(me?.authenticated);

  const cartQuery = useQuery({
    queryKey: ["cart"],
    enabled: authenticated,
    queryFn: async () => {
      const res = await fetchCart();
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
  const cartCount = cartQuery.data?.lines.reduce((n, l) => n + l.qty, 0) ?? 0;

  const [open, setOpen] = useState(false);

  async function onLogout() {
    await logout();
    await meQuery.refetch();
    setOpen(false);
  }

  function onAccountClick() {
    if (authenticated) {
      setOpen((v) => !v);
      return;
    }
    void navigate({
      to: "/login",
      search: { returnTo: pathname || "/" },
    });
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--bg-app)]">
      <ScreenToolbar className="h-12 border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2 outline-none"
          aria-label={t("title")}
        >
          <img src={LOGO} alt="" className="size-7 object-contain" />
          <span className="text-ui-base font-semibold text-[var(--brand-navy)]">
            {t("title")}
          </span>
        </Link>
        <ScreenToolbarSpacer />
        {authenticated ? (
          <span className="hidden max-w-40 truncate text-ui-micro text-[var(--text-muted)] sm:inline">
            {meLabel(me)}
          </span>
        ) : null}
        <div className="relative">
          <button
            type="button"
            onClick={onAccountClick}
            className="inline-flex h-8 w-8 items-center justify-center text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-elevated)] hover:text-[var(--brand-navy)]"
            aria-label={authenticated ? t("accountAria") : t("loginAria")}
            aria-expanded={authenticated ? open : undefined}
            aria-haspopup={authenticated ? "dialog" : undefined}
          >
            <User size={16} weight={authenticated ? "fill" : "regular"} />
          </button>
          {authenticated && open ? (
            <div
              role="dialog"
              aria-label={t("accountTitle")}
              className="absolute right-0 top-full z-50 mt-1 w-72 overflow-hidden rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3 shadow-subtle"
            >
              <div className="flex flex-col gap-3">
                <p className="text-ui-base font-medium text-[var(--brand-navy)]">
                  {meLabel(me)}
                </p>
                <p className="text-ui-micro text-[var(--text-muted)]">
                  {me?.partner_display_name}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  onClick={() => void onLogout()}
                >
                  {t("logout")}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </ScreenToolbar>

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-48 shrink-0 flex-col border-r border-[var(--border-subtle)] bg-[var(--bg-surface)]">
          <nav className="flex flex-col gap-0.5 p-2" aria-label={t("navAria")}>
            {NAV.map((item) => {
              const isActive = active === item.id;
              const showCount =
                item.id === "parts" && authenticated && cartCount > 0;
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  data-active={isActive ? "true" : undefined}
                  className={cn(
                    "flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-ui-dense outline-none",
                    isActive
                      ? "bg-[var(--bg-elevated)] font-semibold text-[var(--brand-navy)]"
                      : "text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--brand-navy)]",
                    item.later && !isActive ? "opacity-80" : null,
                  )}
                >
                  <span className="truncate">{t(item.labelKey)}</span>
                  <span className="flex shrink-0 items-center gap-1">
                    {showCount ? (
                      <span className="font-mono text-ui-micro text-[var(--text-muted)]">
                        {cartCount}
                      </span>
                    ) : null}
                    {item.later ? (
                      <span className="text-ui-nano uppercase tracking-wide text-[var(--text-faint)]">
                        {t("navLater")}
                      </span>
                    ) : null}
                  </span>
                </Link>
              );
            })}
          </nav>
        </aside>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
}

function meLabel(me: MeResponse | undefined): string {
  return me?.display_name || me?.login_name || "";
}
